import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, message, Tabs, Table, Tag,
  Modal, Select, Switch, Space, Checkbox, Progress
} from 'antd';
import { SaveOutlined, PlusOutlined, EditOutlined, DeleteOutlined, FacebookOutlined, EyeOutlined, SyncOutlined, CheckSquareOutlined, SquareOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { TabPane } = Tabs;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [adAccounts, setAdAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [developerAccounts, setDeveloperAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userEditModalVisible, setUserEditModalVisible] = useState(false);
  const [pagesModalVisible, setPagesModalVisible] = useState(false);
  const [accountPages, setAccountPages] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountName, setSelectedAccountName] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [fetchingPages, setFetchingPages] = useState(false);
  const [accountForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [userEditForm] = Form.useForm();
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [batchSyncLoading, setBatchSyncLoading] = useState(false);
  const [batchSyncResult, setBatchSyncResult] = useState(null);

  useEffect(() => {
    fetchAdAccounts();
    fetchUsers();
    fetchDeveloperAccounts();
  }, []);

  const fetchAdAccounts = async () => {
    try {
      const response = await api.get('/ad-accounts/');
      setAdAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch ad accounts:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDeveloperAccounts = async () => {
    try {
      const response = await api.get('/business-managers/developer-accounts/list');
      setDeveloperAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch developer accounts:', error);
    }
  };

  const handleSaveAccount = async (values) => {
    try {
      await api.post('/ad-accounts/', values);
      message.success('账户添加成功');
      setAccountModalVisible(false);
      accountForm.resetFields();
      fetchAdAccounts();
    } catch (error) {
      message.error(error.response?.data?.detail || '添加失败');
    }
  };

  const handleSaveUser = async (values) => {
    try {
      await api.post('/auth/register', values);
      message.success('用户创建成功');
      setUserModalVisible(false);
      userForm.resetFields();
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    }
  };

  const handleUpdateUser = async (values) => {
    try {
      await api.put(`/users/${selectedUserId}`, values);
      message.success('用户更新成功');
      setUserEditModalVisible(false);
      userEditForm.resetFields();
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || '更新失败');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUserId(user.id);
    userEditForm.setFieldsValue({
      ...user,
    });
    setUserEditModalVisible(true);
  };

  const accountColumns = [
    {
      title: (
        <Checkbox
          checked={selectedAccountIds.length === adAccounts.length}
          indeterminate={selectedAccountIds.length > 0 && selectedAccountIds.length < adAccounts.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedAccountIds(adAccounts.map((item) => item.id));
            } else {
              setSelectedAccountIds([]);
            }
          }}
        />
      ),
      key: 'selection',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedAccountIds.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedAccountIds([...selectedAccountIds, record.id]);
            } else {
              setSelectedAccountIds(selectedAccountIds.filter((id) => id !== record.id));
            }
          }}
        />
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
    },
    {
      title: '账户ID',
      dataIndex: 'account_id',
    },
    {
      title: 'BM 信息',
      key: 'bm_info',
      render: (_, record) => (
        <div style={{ lineHeight: '1.5' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
            {record.bm_facebook_id || record.bm_id || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.bm_name || '未知BM'}
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '日API调用',
      dataIndex: 'daily_api_calls',
    },
    {
      title: 'API限制',
      dataIndex: 'daily_api_limit',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<FacebookOutlined />}
            loading={fetchingPages && selectedAccountId === record.id}
            onClick={() => handleFetchPages(record.id)}
          >
            同步信息
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPages(record.id)}
          >
            查看主页
          </Button>
        </Space>
      ),
    },
  ];

  // 获取公共主页
  const handleFetchPages = async (accountId) => {
    const account = adAccounts.find(a => a.id === accountId);
    setSelectedAccountId(accountId);
    setSelectedAccountName(account?.name || '');
    setFetchingPages(true);
    try {
      const response = await api.post(`/ad-accounts/${accountId}/fetch-pages`);
      message.success(response.data.message || '获取成功');
      // 显示获取到的主页
      setAccountPages(response.data.pages || []);
      setPagesModalVisible(true);
    } catch (error) {
      message.error(error.response?.data?.detail || '获取公共主页失败');
    } finally {
      setFetchingPages(false);
      setSelectedAccountId(null);
    }
  };

  // 查看已关联的公共主页
  const handleViewPages = async (accountId) => {
    const account = adAccounts.find(a => a.id === accountId);
    setSelectedAccountName(account?.name || '');
    try {
      const response = await api.get(`/ad-accounts/${accountId}/pages`);
      setAccountPages(response.data || []);
      setPagesModalVisible(true);
    } catch (error) {
      message.error(error.response?.data?.detail || '获取主页列表失败');
    }
  };

  // 批量同步
  const handleBatchSync = async () => {
    if (selectedAccountIds.length === 0) {
      message.warning('请选择至少一个账户');
      return;
    }

    setBatchSyncLoading(true);
    try {
      const response = await api.post('/ad-accounts/batch-sync', selectedAccountIds);
      setBatchSyncResult(response.data);
      message.success(`批量同步完成：成功 ${response.data.success_count} 个，失败 ${response.data.failed_count} 个`);
      fetchAdAccounts();
    } catch (error) {
      message.error(error.response?.data?.detail || '批量同步失败');
    } finally {
      setBatchSyncLoading(false);
    }
  };

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
    },
    {
      title: '姓名',
      dataIndex: 'full_name',
    },
    {
      title: '角色',
      dataIndex: 'role',
      render: (role) => {
        const roleMap = {
          'super_admin': { color: 'red', text: '超级管理员' },
          'manager': { color: 'blue', text: '运营经理' },
          'material_admin': { color: 'green', text: '素材管理员' },
          'analyst': { color: 'orange', text: '数据分析师' },
        };
        const { color, text } = roleMap[role] || { color: 'default', text: role };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '分配的开发者账号',
      dataIndex: 'developer_account_ids',
      render: (developerAccountIds) => {
        if (!developerAccountIds || developerAccountIds.length === 0) {
          return '-';
        }
        return (
          <div>
            {developerAccountIds.slice(0, 3).map(id => {
              const devAccount = developerAccounts.find(da => da.id === id);
              return devAccount ? <Tag key={id}>{devAccount.name}</Tag> : null;
            })}
            {developerAccountIds.length > 3 && (
              <Tag>...等{developerAccountIds.length}个</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: '分配的广告账户',
      dataIndex: 'ad_account_ids',
      render: (adAccountIds) => {
        if (!adAccountIds || adAccountIds.length === 0) {
          return '-';
        }
        return (
          <div>
            {adAccountIds.slice(0, 3).map(id => {
              const adAccount = adAccounts.find(aa => aa.id === id);
              return adAccount ? <Tag key={id}>{adAccount.name}</Tag> : null;
            })}
            {adAccountIds.length > 3 && (
              <Tag>...等{adAccountIds.length}个</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2>系统设置</h2>
      
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="广告账户" key="accounts">
          <Card
            extra={
              <Space>
                {selectedAccountIds.length > 0 && (
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    loading={batchSyncLoading}
                    onClick={handleBatchSync}
                  >
                    批量同步 ({selectedAccountIds.length})
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAccountModalVisible(true)}
                >
                  添加账户
                </Button>
              </Space>
            }
          >
            <Table
              columns={accountColumns}
              dataSource={adAccounts}
              rowKey="id"
            />
          </Card>

          {batchSyncResult && (
            <Card
              title="批量同步结果"
              style={{ marginTop: 16 }}
              bordered={false}
            >
              <div style={{ marginBottom: 16 }}>
                <Progress
                  percent={(batchSyncResult.success_count / batchSyncResult.total_accounts) * 100}
                  status={batchSyncResult.failed_count === 0 ? 'success' : 'exception'}
                  format={(percent) =>
                    `${batchSyncResult.success_count}/${batchSyncResult.total_accounts} 成功`
                  }
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
                  <span>同步主页: {batchSyncResult.total_pages_synced}</span>
                  <span>同步像素: {batchSyncResult.total_pixels_synced}</span>
                </div>
              </div>

              {batchSyncResult.details.length > 0 && (
                <Table
                  dataSource={batchSyncResult.details}
                  rowKey="account_id"
                  pagination={false}
                  columns={[
                    {
                      title: '账户名称',
                      dataIndex: 'account_name',
                    },
                    {
                      title: '状态',
                      dataIndex: 'success',
                      render: (success, record) =>
                        success ? (
                          <Tag color="success">
                            成功 (主页: {record.pages_synced}, 像素: {record.pixels_synced})
                          </Tag>
                        ) : (
                          <Tag color="error">{record.error}</Tag>
                        ),
                    },
                  ]}
                />
              )}
            </Card>
          )}
        </TabPane>

        <TabPane tab="用户管理" key="users">
          <Card
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setUserModalVisible(true)}
              >
                创建用户
              </Button>
            }
          >
            <Table
              columns={userColumns}
              dataSource={users}
              rowKey="id"
            />
          </Card>
        </TabPane>

        <TabPane tab="系统配置" key="system">
          <Card title="基本配置">
            <Form layout="vertical">
              <Form.Item label="系统名称">
                <Input defaultValue="FB Ads Pro" />
              </Form.Item>
              <Form.Item label="API版本">
                <Input defaultValue="v18.0" />
              </Form.Item>
              <Form.Item label="批量大小">
                <Input defaultValue="50" />
              </Form.Item>
              <Form.Item label="速率限制（次/秒）">
                <Input defaultValue="1" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />}>
                  保存配置
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>

      {/* 添加账户模态框 */}
      <Modal
        title="添加广告账户"
        open={accountModalVisible}
        onCancel={() => setAccountModalVisible(false)}
        onOk={() => accountForm.submit()}
      >
        <Form
          form={accountForm}
          layout="vertical"
          onFinish={handleSaveAccount}
        >
          <Form.Item
            name="name"
            label="账户名称"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="account_id"
            label="广告账户ID"
            rules={[{ required: true }]}
          >
            <Input placeholder="act_123456789" />
          </Form.Item>
          <Form.Item
            name="bm_id"
            label="BM ID"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="pixel_id"
            label="Pixel ID"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="app_id"
            label="App ID"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="app_secret"
            label="App Secret"
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="access_token"
            label="Access Token"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建用户模态框 */}
      <Modal
        title="创建用户"
        open={userModalVisible}
        onCancel={() => setUserModalVisible(false)}
        onOk={() => userForm.submit()}
        width={700}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleSaveUser}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="full_name"
            label="姓名"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="manager">运营经理</Select.Option>
              <Select.Option value="material_admin">素材管理员</Select.Option>
              <Select.Option value="analyst">数据分析师</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="developer_account_ids"
            label="分配的开发者账号"
          >
            <Select
              mode="multiple"
              placeholder="请选择开发者账号"
              style={{ width: '100%' }}
            >
              {developerAccounts.map(account => (
                <Select.Option key={account.id} value={account.id}>
                  {account.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="ad_account_ids"
            label="分配的广告账户"
          >
            <Select
              mode="multiple"
              placeholder="请选择广告账户"
              style={{ width: '100%' }}
            >
              {adAccounts.map(account => (
                <Select.Option key={account.id} value={account.id}>
                  {account.name} ({account.account_id})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户模态框 */}
      <Modal
        title="编辑用户"
        open={userEditModalVisible}
        onCancel={() => setUserEditModalVisible(false)}
        onOk={() => userEditForm.submit()}
        width={700}
      >
        <Form
          form={userEditForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: false, min: 6 }]}
            extra="留空表示不修改密码"
          >
            <Input.Password placeholder="留空表示不修改密码" />
          </Form.Item>
          <Form.Item
            name="full_name"
            label="姓名"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="manager">运营经理</Select.Option>
              <Select.Option value="material_admin">素材管理员</Select.Option>
              <Select.Option value="analyst">数据分析师</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="is_active"
            label="状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item
            name="developer_account_ids"
            label="分配的开发者账号"
          >
            <Select
              mode="multiple"
              placeholder="请选择开发者账号"
              style={{ width: '100%' }}
            >
              {developerAccounts.map(account => (
                <Select.Option key={account.id} value={account.id}>
                  {account.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="ad_account_ids"
            label="分配的广告账户"
          >
            <Select
              mode="multiple"
              placeholder="请选择广告账户"
              style={{ width: '100%' }}
            >
              {adAccounts.map(account => (
                <Select.Option key={account.id} value={account.id}>
                  {account.name} ({account.account_id})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 公共主页列表弹窗 */}
      <Modal
        title={`${selectedAccountName} 的公共主页 (${accountPages.length}个)`}
        open={pagesModalVisible}
        onCancel={() => setPagesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPagesModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {accountPages.length > 0 ? (
          <Table
            dataSource={accountPages}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: '主页ID',
                dataIndex: 'page_id',
                key: 'page_id',
              },
              {
                title: '名称',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: '类别',
                dataIndex: 'category',
                key: 'category',
                render: (text) => text || '-',
              },
              {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                render: (status) => (
                  <Tag color={status === 'active' ? 'success' : 'default'}>
                    {status === 'active' ? '启用' : '停用'}
                  </Tag>
                ),
              },
              {
                title: '关联账户',
                dataIndex: 'ad_account_ids',
                key: 'ad_account_ids',
                render: (ids) => (
                  <span>{ids?.length || 0} 个账户</span>
                ),
              },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            <p>暂无公共主页</p>
            <p>请点击「获取主页」按钮从 Facebook 获取</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Settings;
